import {
  type AnyChainflipAsset,
  type BaseChainflipAsset,
  anyAssetConstants,
} from '@chainflip/utils/chainflip';
import { formatTimestampShort } from '@chainflip/utils/date';
import { isNotNullish } from '@chainflip/utils/guard';
import { abbreviate } from '@chainflip/utils/string';
import { tickToRate } from '@chainflip/utils/tickMath';
import BigNumber from 'bignumber.js';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import WhiteSpinnerJSON from '@/shared/animations/spinner-white.json';
import LayersIcon from '@/shared/assets/svg/layers-icon-with-shadow.svg';
import { Link } from '@/shared/components/atoms/Link';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import {
  OrderTypePill,
  OrderPrice,
  SellPill,
  BuyPill,
} from '@/shared/components/orders/RangeOrderRow';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { type GetOrderFillsQuery } from '@/shared/graphql/generated/lp/graphql';
import { getOrderFillsQuery } from '@/shared/graphql/lp/lp';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import DistanceIcon from '@/shared/icons/flip-ui-kit/small/Distance';
import { anyChainflipAssetMap } from '@/shared/utils/env';
import Lottie from '@/shared/utils/Lottie';
import { formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

const MIN_FILLED_VALUE_USD = 1;

const getLimitOrderFillData = (
  node: NonNullable<GetOrderFillsQuery['limitOrderFills']>['nodes'][number],
) => {
  const orderPrice = tickToRate(node.tick, node.baseAsset);
  const filledAsset = node.type === 'Bid' ? 'Usdc' : node.baseAsset;
  const receivedAsset = node.type === 'Bid' ? node.baseAsset : 'Usdc';

  const receivedAmount =
    node.type === 'Bid'
      ? new BigNumber(node.filledAmount)
          .div(orderPrice)
          .shiftedBy(anyAssetConstants[node.baseAsset].decimals - anyAssetConstants.Usdc.decimals)
      : new BigNumber(node.filledAmount)
          .multipliedBy(orderPrice)
          .shiftedBy(anyAssetConstants.Usdc.decimals - anyAssetConstants[node.baseAsset].decimals);

  return {
    id: node.id,
    timestamp: new Date(node.blockTimestamp ?? 0),
    type: 'LIMIT' as const,
    baseAsset: node.baseAsset as BaseChainflipAsset,
    side: node.type,
    tick: node.tick,
    filledAsset,
    filledAmount: TokenAmount.fromAsset(node.filledAmount, filledAsset),
    filledAmountValueUsd: Number(node.filledAmountValueUsd),
    receivedAsset,
    receivedAmount: TokenAmount.fromAsset(receivedAmount, receivedAsset),
    lp: node.lp?.idSs58 ?? undefined,
  };
};

const getRangeOrderFillData = (
  node: NonNullable<GetOrderFillsQuery['rangeOrderFills']>['nodes'][number],
  filledAsset: AnyChainflipAsset,
) => {
  const receivedAsset = filledAsset === 'Usdc' ? node.baseAsset : 'Usdc';
  return {
    id: `${node.id}-${filledAsset}`,
    timestamp: new Date(node.blockTimestamp ?? 0),
    type: 'RANGE' as const,
    baseAsset: node.baseAsset as BaseChainflipAsset,
    lowerTick: node.rangeStartTick,
    upperTick: node.rangeEndTick,
    filledAsset,
    filledAmount: TokenAmount.fromAsset(
      filledAsset === 'Usdc' ? node.quoteFilledAmount : node.baseFilledAmount,
      filledAsset,
    ),
    filledAmountValueUsd:
      filledAsset === 'Usdc'
        ? Number(node.quoteFilledAmountValueUsd)
        : Number(node.baseFilledAmountValueUsd),
    receivedAsset,
    receivedAmount: TokenAmount.fromAsset(
      receivedAsset === 'Usdc' ? node.quoteReceivedAmount : node.baseReceivedAmount,
      receivedAsset,
    ),
    feesAsset: receivedAsset,
    feesEarned: TokenAmount.fromAsset(
      receivedAsset === 'Usdc' ? node.quoteFeesEarned : node.baseFeesEarned,
      receivedAsset,
    ),
    lp: node.lp?.idSs58 ?? undefined,
  };
};

export const extractOrderFills = (data: GetOrderFillsQuery) => {
  const limitFills = (data?.limitOrderFills?.nodes ?? []).map(getLimitOrderFillData);
  const rangeFills = (data?.rangeOrderFills?.nodes ?? [])
    .flatMap((node) => [
      Number(node.baseFilledAmount) > 0 ? getRangeOrderFillData(node, node.baseAsset) : null,
      Number(node.quoteFilledAmount) > 0 ? getRangeOrderFillData(node, 'Usdc') : null,
    ])
    .filter(isNotNullish);

  return [...limitFills, ...rangeFills].toSorted(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
  );
};

const RangeOrderFillRow = ({
  data: {
    baseAsset,
    lowerTick,
    upperTick,
    filledAsset: fillAsset,
    filledAmount,
    filledAmountValueUsd: fillValueUsd,
    receivedAsset,
    receivedAmount,
    feesEarned,
    timestamp,
    lp,
  },
  onClick,
  showLp,
}: {
  data: ReturnType<typeof getRangeOrderFillData>;
  onClick?: (baseAsset: BaseChainflipAsset) => void;
  showLp: boolean;
}) => (
  <ClickableTableRow
    disableHoverStyle={!onClick}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(baseAsset);
    }}
  >
    <td className="whitespace-nowrap">
      <OrderTypePill type="RANGE" />
    </td>
    <td className="whitespace-nowrap text-12">
      <div className="flex flex-col gap-y-2 text-cf-light-3">
        <span className="flex gap-x-2">
          <TokenWithChainByAsset asset={fillAsset} /> ~{filledAmount?.toFormatted()}{' '}
          {anyChainflipAssetMap[fillAsset].symbol}
        </span>
      </div>
    </td>
    <td className="whitespace-nowrap text-12">
      <div className="flex flex-col gap-y-2 text-cf-light-3">
        <span className="flex gap-x-2">
          <TokenWithChainByAsset asset={receivedAsset} /> {receivedAmount?.toFormatted()}{' '}
          {anyChainflipAssetMap[receivedAsset].symbol}
          <span className="text-cf-green-3">(+{feesEarned?.toFormatted()})</span>
        </span>
      </div>
    </td>
    <td className="flex whitespace-nowrap text-12">
      <div className="flex flex-col items-center">
        <OrderPrice baseAsset={baseAsset} tick={lowerTick} />
        <DistanceIcon className="text-cf-light-1" />
        <OrderPrice baseAsset={baseAsset} tick={upperTick} />
      </div>
    </td>
    <td className="whitespace-nowrap text-12">{formatUsdValue(fillValueUsd)}</td>
    {showLp ? (
      <td className="whitespace-nowrap text-12">
        {lp ? (
          <Link href={`/lps/${lp}`} noPropagate underline>
            <span>{abbreviate(lp)}</span>
          </Link>
        ) : (
          '-'
        )}
      </td>
    ) : null}
    <td className="whitespace-nowrap text-right text-12">
      {formatTimestampShort(timestamp.toISOString())}
    </td>
  </ClickableTableRow>
);

const LimitOrderFillRow = ({
  data: {
    baseAsset,
    side,
    filledAsset,
    filledAmount,
    receivedAsset,
    receivedAmount,
    tick,
    filledAmountValueUsd,
    timestamp,
    lp,
  },
  onClick,
  showLp,
}: {
  data: ReturnType<typeof getLimitOrderFillData>;
  onClick?: (asset: BaseChainflipAsset) => void;
  showLp: boolean;
}) => (
  <ClickableTableRow
    disableHoverStyle={!onClick}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(baseAsset);
    }}
  >
    <td className="whitespace-nowrap text-12">
      <div className="flex flex-col justify-start gap-y-2 whitespace-nowrap">
        {side === 'Ask' ? <SellPill /> : <BuyPill />}
        <OrderTypePill type="LIMIT" />
      </div>
    </td>
    <td className="whitespace-nowrap text-12">
      <div className="flex items-center gap-x-2 text-cf-light-3">
        <div className="flex flex-col gap-y-2">
          <span className="flex gap-x-2">
            <TokenWithChainByAsset asset={filledAsset} />
            <span>{filledAmount?.toFormatted()}</span>
            <span>{anyChainflipAssetMap[filledAsset].symbol}</span>
          </span>
        </div>
      </div>
    </td>
    <td className="whitespace-nowrap text-12">
      <div className="flex items-center gap-x-2 text-cf-light-3">
        <div className="flex flex-col gap-y-2">
          <span className="flex gap-x-2">
            <TokenWithChainByAsset asset={receivedAsset} />
            <span>{receivedAmount?.toFormatted()}</span>
            <span>{anyChainflipAssetMap[receivedAsset].symbol}</span>
          </span>
        </div>
      </div>
    </td>
    <td className="whitespace-nowrap text-12">
      <OrderPrice baseAsset={baseAsset} tick={tick} />
    </td>
    <td className="whitespace-nowrap text-12">{formatUsdValue(filledAmountValueUsd)}</td>
    {showLp ? (
      <td className="whitespace-nowrap text-12">
        {lp ? (
          <Link href={`/lps/${lp}`} noPropagate underline>
            <span>{abbreviate(lp)}</span>
          </Link>
        ) : (
          '-'
        )}
      </td>
    ) : null}
    <td className="whitespace-nowrap text-right text-12">
      {formatTimestampShort(timestamp.toISOString())}
    </td>
  </ClickableTableRow>
);

export default function OrderFillsTable({
  lpId,
  asset,
  className,
  containerClassName,
  hideSmallAmounts = false,
}: {
  lpId?: number;
  asset?: AnyChainflipAsset;
  className?: string;
  containerClassName?: string;
  hideSmallAmounts?: boolean;
}) {
  const { data: fills, isLoading } = useGqlQuery(getOrderFillsQuery, {
    context: { clientName: 'lpProcessor' },
    refetchInterval: TABLE_POLL_INTERVAL,
    enabled: Boolean(lpId) || Boolean(asset),
    variables: {
      id: lpId,
      asset,
      minFilledValueUsd: hideSmallAmounts ? MIN_FILLED_VALUE_USD.toString() : undefined,
      first: 100,
    },
    select: extractOrderFills,
  });

  return (
    <MacScrollbar
      skin="dark"
      className={classNames(
        'h-full max-h-[405px] overflow-y-scroll rounded-b-md border-b border-b-cf-gray-3-5',
        containerClassName,
      )}
    >
      <TableV2
        columns={[
          'Type',
          'Filled Amount',
          'Received Amount',
          'Price / Range',
          'Value',
          ...['Liquidity Provider'].filter(() => lpId == null),
          { alignment: 'right', name: 'Time' },
        ]}
        emptyRowsContent={
          isLoading ? (
            <div className="m-10 flex flex-col items-center gap-y-2 py-4">
              <Lottie
                as="div"
                className="mr-2 h-5 w-5"
                animationData={WhiteSpinnerJSON}
                autoplay
                loop
              />
            </div>
          ) : (
            <div className="m-10 flex flex-col items-center gap-y-2 py-4">
              <LayersIcon />
              <span className="flex flex-col items-center text-14 text-cf-light-2">
                No positions here yet.
              </span>
            </div>
          )
        }
        rows={
          fills?.map((fill) =>
            fill.type === 'LIMIT' ? (
              <LimitOrderFillRow key={fill.id} data={fill} showLp={lpId == null} />
            ) : (
              <RangeOrderFillRow key={fill.id} data={fill} showLp={lpId == null} />
            ),
          ) || []
        }
        className={className}
      />
    </MacScrollbar>
  );
}
